import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: always read latest token from localStorage so the
// Authorization header survives full-page reloads and tab restores.
// Also injects X-Active-Tenant-Id so super-admins viewing a specific tenant
// see ONLY that tenant's data (backend enforces super-admin role check).
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('crm_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeTenant = localStorage.getItem('crm_active_tenant_id');
    if (activeTenant) {
      config.headers = config.headers || {};
      config.headers['X-Active-Tenant-Id'] = activeTenant;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track redirect so we don't fire toast/redirect more than once during a burst
// of parallel 401 responses.
let isHandling401 = false;

// Response interceptor: gracefully handle expired/invalid JWT.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || '';
    const url = error?.config?.url || '';

    const isAuthExpired =
      status === 401 &&
      // Don't auto-redirect for the actual login call — let the login page
      // surface the error message inline.
      !url.includes('/auth/login') &&
      (typeof detail !== 'string' || !detail.toLowerCase().includes('invalid credentials'));

    if (isAuthExpired && !isHandling401) {
      isHandling401 = true;
      try {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        localStorage.removeItem('crm_tenant_id');
        delete api.defaults.headers.common['Authorization'];
        toast.error('انتهت الجلسة، الرجاء تسجيل الدخول مجدداً');
      } finally {
        // Slight delay so the toast is visible before navigation.
        setTimeout(() => {
          isHandling401 = false;
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/login');
          }
        }, 600);
      }
    }
    return Promise.reject(error);
  }
);

// Clients API
export const securityApi = {
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  changeEmail: (password, new_email) => api.post('/auth/change-email', { password, new_email }),
};

export const financeApi = {
  summary: () => api.get('/finance/summary'),
  walletBalances: () => api.get('/finance/wallet-balances'),
  transactions: (params) => api.get('/finance/transactions', { params }),
  addTransaction: (data) => api.post('/finance/transactions', data),
  updateTransaction: (id, data) => api.patch(`/finance/transactions/${id}`, data),
  reserveLedger: () => api.get('/finance/reserve-ledger'),
  deleteTransaction: (id) => api.delete(`/finance/transactions/${id}`),
  restoreTransaction: (id) => api.post(`/finance/transactions/${id}/restore`),
  cheques: () => api.get('/finance/cheques'),
  addCheque: (data) => api.post('/finance/cheques', data),
  updateCheque: (id, data) => api.patch(`/finance/cheques/${id}`, data),
  deleteCheque: (id) => api.delete(`/finance/cheques/${id}`),
  sendReminders: () => api.post('/finance/cheques/send-reminders'),
  aiAdvice: () => api.post('/finance/ai-advice'),
  twilioOverview: () => api.get('/twilio/overview'),
  twilioMessages: (limit = 100) => api.get('/twilio/messages', { params: { limit } }),
  twilioCalls: (limit = 100) => api.get('/twilio/calls', { params: { limit } }),
  resendEmails: () => api.get('/twilio/emails'),
  downloadReport: (start, end) => api.get('/finance/report', { params: { start, end }, responseType: 'blob' }),
  rates: () => api.get('/finance/rates'),
  audit: (limit = 100) => api.get('/finance/audit', { params: { limit } }),
  voiceEntry: (formData) => api.post('/finance/voice-entry', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  voiceTranscribe: (formData) => api.post('/finance/voice-transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  voiceParse: (transcript) => api.post('/finance/voice-parse', { transcript }, { timeout: 60000 }),
  accessMe: () => api.get('/finance/access/me'),
  accessList: () => api.get('/finance/access'),
  accessSave: (user_ids) => api.put('/finance/access', { user_ids }),
  settings: () => api.get('/finance/settings'),
  saveSettings: (payload) => api.put('/finance/settings', payload),
  sendMonthlyReportNow: () => api.post('/finance/monthly-report/send-now', {}, { timeout: 90000 }),
  motivationToday: () => api.get('/motivation/today'),
  leaderboard: () => api.get('/motivation/leaderboard'),
  exportExcel: (start, end) => api.get('/finance/export/excel', { params: start && end ? { start, end } : {}, responseType: 'blob', timeout: 60000 }),
  uploadReceipt: (txId, formData) => api.post(`/finance/transactions/${txId}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  pocketInsights: () => api.get('/finance/pocket-insights'),
  pushPublicKey: () => api.get('/finance/push/public-key'),
  pushSubscribe: (data) => api.post('/finance/push/subscribe', data),
  pushUnsubscribe: (data) => api.post('/finance/push/unsubscribe', data),
  pushTest: () => api.post('/finance/push/test'),
  pocketCategories: () => api.get('/finance/pocket-categories'),
  savePocketCategories: (categories) => api.put('/finance/pocket-categories', { categories }),
  getReceipt: (receiptId) => api.get(`/finance/receipts/${receiptId}`, { responseType: 'blob', timeout: 30000 }),
};

export const clientsApi = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkDelete: (leadIds) => api.post('/clients/bulk-delete', { lead_ids: leadIds }),
  bulkUpdateStatus: (leadIds, status) => api.post('/clients/bulk-update-status', { lead_ids: leadIds, status }),
  bulkAssign: (leadIds, salesman_id) => api.post('/clients/bulk-assign', { lead_ids: leadIds, salesman_id }),
  // Lightweight counts for the FX/LEGAL filter chips above the Leads table.
  // Backend respects the per-agent visibility gate.
  categoryCounts: (params) => api.get('/clients/category-counts', { params }),
  statusCounts: (params) => api.get('/clients/status-counts', { params }),
  // Custom lead statuses (user-created, persist globally)
  customStatuses: () => api.get('/custom-statuses'),
  createCustomStatus: (payload) => api.post('/custom-statuses', typeof payload === 'string' ? { label: payload } : payload),
  updateCustomStatus: (value, payload) => api.put(`/custom-statuses/${encodeURIComponent(value)}`, payload),
  reorderCustomStatuses: (values) => api.post('/custom-statuses/reorder', { values }),
  deleteCustomStatus: (value) => api.delete(`/custom-statuses/${encodeURIComponent(value)}`),
  // Round-robin auto-distribution. Pass EITHER an explicit salesman_ids array
  // OR a role (+ optional lead_category) to auto-pick the pool.
  // Backend returns { distribution: [{salesman_id, salesman_name, count}, ...] }
  bulkAssignRoundRobin: (leadIds, { salesman_ids = null, role = null, lead_category = null } = {}) =>
    api.post('/clients/bulk-assign-round-robin', {
      lead_ids: leadIds,
      ...(salesman_ids ? { salesman_ids } : {}),
      ...(role ? { role } : {}),
      ...(lead_category ? { lead_category } : {}),
    }),
  bulkAddTag: (leadIds, tag) => api.post('/clients/bulk-add-tag', { lead_ids: leadIds, tag }),
  bulkRemoveTag: (leadIds, tag) => api.post('/clients/bulk-remove-tag', { lead_ids: leadIds, tag }),
  bulkMarkContacted: (leadIds) => api.post('/clients/bulk-mark-contacted', { lead_ids: leadIds }),
  bulkMarkNew: (leadIds) => api.post('/clients/bulk-mark-new', { lead_ids: leadIds }),
  bulkArchive: (leadIds) => api.post('/clients/bulk-archive', { lead_ids: leadIds }),
  bulkExport: (leadIds) => api.post('/clients/bulk-export', { lead_ids: leadIds }),
  generateAccount: (id) => api.post(`/clients/${id}/generate-account`),
  shuffleAssign: (clientIds, salesmanIds) => 
    api.post('/clients/shuffle-assign', { client_ids: clientIds, salesman_ids: salesmanIds }),
};

// Users API
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  block: (id) => api.post(`/users/${id}/block`),
  unblock: (id) => api.post(`/users/${id}/unblock`),
  delete: (id) => api.delete(`/users/${id}`),
};

// Per-agent KPI dashboard (admin-only)
export const agentsApi = {
  kpi: (params = {}) => api.get('/agents/kpi', { params }),
};

// Campaigns API
export const campaignsApi = {
  list: (params) => api.get('/campaigns', { params }),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
};

// Transactions API
export const transactionsApi = {
  list: (params) => api.get('/transactions', { params }),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  getStats: (params) => api.get('/banking/stats', { params }),
};

// Comments API
export const commentsApi = {
  list: (clientId) => api.get('/comments', { params: { client_id: clientId } }),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.patch(`/comments/${id}`, data),
  remove: (id) => api.delete(`/comments/${id}`),
  history: (id) => api.get(`/comments/${id}/history`),
};

// Documents API
export const documentsApi = {
  list: (clientId) => api.get('/documents', { params: { client_id: clientId } }),
  upload: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Timeline API
export const timelineApi = {
  get: (clientId, limit = 50) => api.get(`/timeline/${clientId}`, { params: { limit } }),
};

// Email Configs API
export const emailConfigsApi = {
  list: () => api.get('/email-configs'),
  get: (userId) => api.get(`/email-configs/${userId}`),
  create: (data) => api.post('/email-configs', data),
  update: (userId, data) => api.put(`/email-configs/${userId}`, data),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentLeads: (limit = 10) => api.get('/dashboard/recent-leads', { params: { limit } }),
};

// Export API
export const exportApi = {
  clients: (params) => api.get('/export/clients', { params }),
};

// Seed API (for development)
export const seedApi = {
  seed: () => api.post('/seed'),
};

// AI Features API
export const aiApi = {
  // Shadow Salesperson
  shadowChat: (conversationId, message, clientId = null) => 
    api.post('/ai/shadow-chat', null, { 
      params: { conversation_id: conversationId, message, client_id: clientId } 
    }),
  getConversations: (limit = 20) => api.get('/ai/conversations', { params: { limit } }),
  
  // Crystal Ball - Predictions
  getCrystalBall: () => api.get('/ai/crystal-ball'),
  
  // Whale Detector
  getWhales: () => api.get('/ai/whale-detector'),
  
  // DNA Profiling
  getDNAProfiles: () => api.get('/ai/dna-profiles'),
  
  // Intent Detection
  getIntentSignals: () => api.get('/ai/intent-detection'),
  
  // Revenue Maximizer
  getRevenueOpportunities: () => api.get('/ai/revenue-maximizer'),
};

// Trading Platform Access API
export const tradingAccessApi = {
  // Create trading access for a client
  createAccess: (clientId, { username, password, autoGenerate = true } = {}) => 
    api.post(`/clients/${clientId}/trading-access`, null, {
      params: { username, password, auto_generate: autoGenerate }
    }),
  
  // Get trading access status
  getStatus: (clientId) => api.get(`/clients/${clientId}/trading-access`),
  
  // Revoke trading access
  revokeAccess: (clientId) => api.delete(`/clients/${clientId}/trading-access`),
  
  // Reset password
  resetPassword: (clientId, newPassword = null) => 
    api.put(`/clients/${clientId}/trading-access/reset-password`, null, {
      params: { new_password: newPassword }
    }),
  
  // List all trading users (admin)
  listUsers: (enabledOnly = true) => 
    api.get('/trading/users', { params: { enabled_only: enabledOnly } }),
  
  // Bulk generate credentials
  bulkGenerate: (clientIds) => 
    api.post('/trading/bulk-generate', clientIds),
  
  // Get trading activity for a client
  getActivity: (tradingUsername, limit = 50) =>
    api.get(`/trading/activity/${tradingUsername}`, { params: { limit } }),
};

// Platform Access (CRM <-> Trading Platform sync)
export const platformAccessApi = {
  lookup: (leadId) => api.get(`/platform-access/lookup/${leadId}`),
  getCredentials: (leadId) => api.get(`/platform-access/credentials/${leadId}`),
  provision: (data) => api.post('/platform-access/provision', data),
  // Partial credentials update — accepts {username?, password?}
  updateCredentials: (leadId, data) => api.patch(`/platform-access/credentials/${leadId}`, data),
  // Returns a unique `firstname.lastname###` suggestion for the lead
  suggestUsername: (leadId) => api.get(`/platform-access/suggest-username/${leadId}`),
  resetPassword: (data) => api.post('/platform-access/reset-password', data),
  ssoLogin: (data) => api.post('/platform-access/sso-login', data),
  // NEW — require corresponding endpoints on the platform repo (see PLATFORM_PATCH.md)
  deposit: (leadId, amount, type = 'deposit', reason = 'CRM deposit') =>
    api.post('/platform-access/deposit', { lead_id: leadId, amount, type, reason }),
  withdraw: (leadId, amount, reason = 'CRM withdrawal') =>
    api.post('/platform-access/withdraw', { lead_id: leadId, amount, reason }),
  updateLeverage: (leadId, leverage) =>
    api.post('/platform-access/update-leverage', { lead_id: leadId, leverage }),
  updateRestrictions: (leadId, restrictions) =>
    api.post('/platform-access/update-restrictions', { lead_id: leadId, ...restrictions }),
  updateUsername: (leadId, newUsername) =>
    api.post('/platform-access/update-username', { lead_id: leadId, new_username: newUsername }),
  getPositions: (leadId) => api.get(`/platform-access/positions/${leadId}`),
  closePosition: (positionId, closePrice = null, reason = 'Force close by CRM') =>
    api.post('/platform-access/close-position', { position_id: positionId, close_price: closePrice, reason }),
};

// Messages API
// ── WhatsApp Widget (built-in WhatsApp client, admin only) ──
export const waWidgetApi = {
  settings: () => api.get('/whatsapp/widget/settings'),
  saveSettings: (data) => api.put('/whatsapp/widget/settings', data),
  channels: () => api.get('/whatsapp/widget/channels'),
  chats: (cid, params) => api.get(`/whatsapp/widget/${cid}/chats`, { params }),
  messages: (cid, chatId, params) => api.get(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/messages`, { params }),
  info: (cid, chatId) => api.get(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/info`),
  chatMedia: (cid, chatId) => api.get(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/media`, { timeout: 90000 }),
  read: (cid, chatId) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/read`),
  presence: (cid, chatId, presence) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/presence`, { presence }),
  subscribePresence: (cid, chatId) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/subscribe-presence`),
  sendText: (cid, chatId, body, quoted) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/send`, { body, quoted: quoted || null }),
  suggestReply: (cid, chatId) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/suggest-reply`, {}, { timeout: 120000 }),
  sendMedia: (cid, chatId, file, kind, caption, quoted, onProgress) => {
    const fd = new FormData();
    fd.append('file', file, file.name || 'file');
    fd.append('kind', kind);
    fd.append('caption', caption || '');
    fd.append('quoted', quoted || '');
    return api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/send-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000, onUploadProgress: onProgress });
  },
  react: (cid, mid, emoji) => api.put(`/whatsapp/widget/${cid}/messages/${encodeURIComponent(mid)}/reaction`, { emoji }),
  deleteMessage: (cid, mid) => api.delete(`/whatsapp/widget/${cid}/messages/${encodeURIComponent(mid)}`),
  linkLead: (cid, chatId, data) => api.post(`/whatsapp/widget/${cid}/chats/${encodeURIComponent(chatId)}/link-lead`, data),
  leadSearch: (q) => api.get('/whatsapp/widget/lead-search', { params: { q } }),
  crmSearch: (cid, q) => api.get(`/whatsapp/widget/${cid}/crm-search`, { params: { q } }),
  activeCalls: (cid) => api.get(`/whatsapp/widget/${cid}/calls/active`),
  welcomeLog: (cid, limit = 200) => api.get(`/whatsapp/widget/${cid}/admin/welcome/log`, { params: { limit } }),
  mediaUrl: (cid, mediaId, mime, name, download) => {
    const t = localStorage.getItem('crm_token') || '';
    const p = new URLSearchParams({ token: t });
    if (mime) p.set('mime', mime);
    if (name) p.set('name', name);
    if (download) p.set('download', '1');
    return `${API_BASE}/api/whatsapp/widget/${cid}/media/${encodeURIComponent(mediaId)}?${p.toString()}`;
  },
};

export const messagesApi = {
  // Conversations
  listConversations: () => api.get('/messages/conversations'),
  onlineUsers: () => api.get('/messages/online'),
  createConversation: (data) => api.post('/messages/conversations', data),
  getConversation: (id) => api.get(`/messages/conversations/${id}`),
  deleteConversation: (id) => api.delete(`/messages/conversations/${id}`),
  
  // Messages
  getMessages: (conversationId, limit = 50, before = null) => 
    api.get(`/messages/conversations/${conversationId}/messages`, { 
      params: { limit, before } 
    }),
  sendMessage: (conversationId, text, attachments = []) => 
    api.post(`/messages/conversations/${conversationId}/messages`, { text, attachments }),
  markRead: (conversationId) => 
    api.put(`/messages/conversations/${conversationId}/read`),
  
  // Team users for messaging
  listTeamUsers: () => api.get('/messages/users'),

  // Reactions
  reactMessage: (convId, msgId, emoji) => api.post(`/messages/conversations/${convId}/messages/${msgId}/react`, { emoji }),

  // Web push (desktop notifications when the CRM window is closed)
  chatPushPublicKey: () => api.get('/messages/push/public-key'),
  chatPushSubscribe: (data) => api.post('/messages/push/subscribe', data),
  chatPushUnsubscribe: (data) => api.post('/messages/push/unsubscribe', data),
  
  // File uploads
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/messages/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/messages/files/${fileId}`,
};

// WebSocket connection for real-time messaging
export const createMessageWebSocket = (userId) => {
  const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
  return new WebSocket(`${wsUrl}/api/ws/chat/${userId}`);
};

export { API_BASE };
export const aiTrainingApi = {
  items: () => api.get('/ai-training/items'),
  profile: () => api.get('/ai-training/profile'),
  addText: (title, content) => api.post('/ai-training/text', { title, content }),
  addAudio: (file, title, onProgress) => { const fd = new FormData(); fd.append('file', file, file.name || 'recording'); fd.append('title', title || ''); return api.post('/ai-training/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000, onUploadProgress: onProgress }); },
  remove: (id) => api.delete(`/ai-training/items/${id}`),
  reprocess: (id) => api.post(`/ai-training/items/${id}/reprocess`),
  rebuild: () => api.post('/ai-training/rebuild', {}, { timeout: 300000 }),
  tryReply: (client_message, client_type) => api.post('/ai-training/try', { client_message, client_type: client_type || null }, { timeout: 180000 }),
};

export default api;
